/* The task the page was served as, if any.
 *
 * Each slug in tasks.json is a permanent entrance to this same application.
 * The page generated for it carries its own title, H1, tutorial and questions
 * in the HTML, and one line of configuration:
 *
 *     <script>window.OIO_TASK = {slug, h1, config};</script>
 *
 * That is the ONLY copy of it. Nothing here restates what a task is or what it
 * configures -- read it out of window and apply it. Adding a task means adding
 * a row to tasks.json and nothing else.
 *
 * Applied in the store's state() rather than after mounting, and that is
 * load-bearing: addFiles() opens with uiStore.$reset(), and $reset() rebuilds
 * from state(). Configure the app anywhere else and the settings survive
 * exactly until the visitor drops their first image -- which is to say, until
 * the moment they matter.
 */

// What a task page may preset. Deliberately a list rather than "anything in
// config": a task page is a static file and its config is untrusted input, so
// a new key is a decision each time.
//
// flipH and flipV were added for the flip-image task, where arriving with the
// mirror already ticked IS the page -- they fall through to the boolean branch
// at the bottom of applyTaskToState().
const ALLOWED = ["formats", "pictureElements", "keepExif", "preset", "pct", "cmpr",
                 "flipH", "flipV", "overlayColor", "overlayOpacity",
                 "watermarkText", "watermarkPosition", "watermarkSize",
                 "watermarkOpacity", "watermarkColor", "watermarkFont",
                 // FOUR caption areas. Spelled out rather than derived from
                 // MEME_SLOTS because this file is evaluated whole by
                 // tests/task_config_test.mjs, which strips `export` and runs
                 // it in a new Function -- an import here is a syntax error
                 // there. tests/meme_layout_test.mjs holds the two in step.
                 "memeTop", "memeBottom", "memeLeft", "memeRight",
                 "memeFont", "memeUpper",
                 "memeTopSize", "memeBottomSize",
                 "memeLeftSize", "memeRightSize",
                 "memeTopColor", "memeBottomColor",
                 "memeLeftColor", "memeRightColor",
                 "asciiCols", "asciiRamp", "asciiColours", "asciiContrast",
                 "asciiEdges", "asciiNoBg",
                 "sheetColumns", "sheetLabels", "sheetMode", "sheetGap",
                 "inspect",
                 "tileCols", "tileRows"]

/* The same four areas, split by what KIND of value each key holds, so
 * applyTaskToState()'s branches below do not grow a conditional per area. */
const MEME_TEXT_KEYS = ["memeTop", "memeBottom", "memeLeft", "memeRight"]
const MEME_COLOR_KEYS = MEME_TEXT_KEYS.map((k) => k + "Color")
const MEME_SIZE_KEYS = MEME_TEXT_KEYS.map((k) => k + "Size")

/* The words the application should use while it is standing in for a task.
 * They live in the registry beside the rest of that task's words; this only
 * reads them, and falls back to the homepage wording so a task that leaves one
 * out still says something sensible. */
/* Does this page turn a picture into text?
 *
 * Presence of asciiCols, mirroring taskOffersWatermark() and taskOffersMeme():
 * the registry says what a page is for, and the interface reads it, rather
 * than either of them testing the slug. A slug test is how /meme-generator
 * would have ended up named in five files.
 *
 * This one changes more than the others do. Every other tool here produces a
 * FILE -- a stage, a versions table, a saving, a Download. This one produces
 * TEXT, so the panel below the stage replaces all of that rather than adding
 * to it: there is nothing to put in a table and no bytes to compare, and
 * printing a saving would be the `--11%` fault again (a figure whose meaning
 * does not survive the artefact changing).
 */
export function taskOffersAscii() {
    const task = activeTask()
    const cfg = (task && task.config) || {}
    return Object.prototype.hasOwnProperty.call(cfg, "asciiCols")
}

/* Does this page lay a SET out on one sheet?
 *
 * Presence of sheetColumns, the same rule taskOffersMeme() and
 * taskOffersAscii() follow: the registry says what a page is for and the
 * interface reads it, rather than either of them testing the slug.
 *
 * It is a batch page like any other -- the pictures are uploaded, optimised
 * and listed in the table exactly as /bulk-image-compressor's are. What this
 * adds is one more thing to do with the finished set, beside the zip and the
 * PDF, so there is no separate pipeline and nothing new is uploaded for it.
 */
export function taskOffersSheet() {
    const task = activeTask()
    const cfg = (task && task.config) || {}
    return Object.prototype.hasOwnProperty.call(cfg, "sheetColumns")
}

/* Does this page cut ONE picture into many?
 *
 * The opposite errand, and the opposite shape: a single-image page whose
 * output is a zip. Nothing is uploaded at all -- the cut happens on a canvas,
 * the way /svg-to-png and /mp4-to-gif work -- so this page never reaches the
 * upload path and the versions table has nothing to show.
 */
export function taskOffersTiles() {
    const task = activeTask()
    const cfg = (task && task.config) || {}
    return Object.prototype.hasOwnProperty.call(cfg, "tileCols")
}

/* Does this page READ a picture rather than make one?
 *
 * Presence of `inspect`, the rule taskOffersAscii() and taskOffersTiles()
 * follow: the registry says what a page is for and the interface reads it,
 * rather than either of them testing a slug.
 *
 * This one goes further than ASCII does. That page produces TEXT instead of a
 * file; this one produces NO ARTEFACT AT ALL -- there is nothing to download,
 * nothing to optimise, and nothing is uploaded. So the whole apparatus below
 * the stage goes: no options panel, no versions table, no saving, no primary
 * button. What replaces it is a report about the file the visitor already
 * has.
 */
export function taskOffersInspect() {
    const task = activeTask()
    const cfg = (task && task.config) || {}
    return Object.prototype.hasOwnProperty.call(cfg, "inspect")
}

export function taskWords(fallbacks, locale) {
    const task = activeTask()
    const ui = (task && task.ui) || {}
    const pageLang = (task && task.lang) || "en"

    // The registry's words are written in the language the PAGE was generated
    // in. If the visitor is reading in a different one they are simply the
    // wrong language, and a generic sentence in the right language beats a
    // specific one in the wrong one -- "Arrastra aquí tus imágenes" over "Drop
    // in the pages you photographed" on a page being read in Spanish.
    //
    // This is a plaster, and worth naming as one. It exists because the
    // interface language is a preference while the page's prose is baked in at
    // build time, so the two can disagree. Putting the language in the URL is
    // what actually fixes it: one URL, one language, everything on it agreeing.
    const sameLanguage = !locale || locale === pageLang
    const pick = (k) => (sameLanguage && typeof ui[k] === "string" && ui[k].trim())
        ? ui[k] : fallbacks[k]

    return {
        dropzone: pick("dropzone"),
        cta: pick("cta"),
        /* The button's words WHEN THE OUTPUT FORMAT IS STILL A CHOICE.
         *
         * A conversion page names its format in the button -- "Turn the photos
         * into JPEGs" -- and then offers a format picker underneath it. Tick
         * PNG and the button is simply wrong, which is what was reported: a
         * results table reading PNG under a button promising JPEGs.
         *
         * So a page may declare a TEMPLATE carrying {format} instead. It is
         * used only while exactly one format is chosen; past that the plural
         * generic already wins, for the reason primaryLabel() records. */
        ctaFormat: pick("ctaFormat"),
        configured: pick("configured"),
    }
}

/* What this page takes in: the kind of file, and how many.
 *
 * One declaration behind every question the interface asks about its input.
 * Which noun the drop zone uses. Whether a second file is refused. Whether six
 * stock photographs demonstrate anything here. Those were three separate
 * answers in three places, which is why each was wrong on a different page.
 *
 * VALIDATED, and it falls back rather than failing. A task page is a static
 * file on a CDN and its config is untrusted input -- the same reason ALLOWED
 * below is a whitelist. A stale or hand-edited declaration must land on
 * today's behaviour, never on a drop zone that accepts nothing: the failure
 * to avoid is a bad deploy quietly taking the site out.
 *
 * `max` comes back null when unusable, and the caller supplies the global cap.
 * This module deliberately does not know what that number is.
 */
const KINDS = ["image", "pdf", "png", "webp", "gif", "video", "svg", "heic", "jpg"]

/* What each kind will actually take, as the file picker's accept attribute and
 * as a pair of predicates for the code that has to agree with it.
 *
 * The narrowing is deliberately modest. A page named for a format still takes
 * any image, because refusing a JPEG on "PNG to WebP" helps nobody -- the
 * visitor wants it converted and the tool can. What a named page does NOT take
 * is a PDF: rasterising a document into pages on a page about WebP is a
 * different job wearing this one's clothes, and until now every page offered
 * it because one hard-coded attribute said so.
 *
 * The attribute and the predicates come from the same table on purpose. They
 * were separate before -- accept="..." in the template, a filter expression in
 * the composition -- and two statements of one rule is how they drift.
 */
/* WHAT AN iPHONE HANDS YOU, spelled out for every page that takes pictures.
 *
 * Two separate problems, one string. `file.type` for a .heic is the operating
 * system's extension map and is routinely EMPTY on Windows, so the intake
 * test `type.includes('image')` refuses it -- reported as "Sorry. Doesn't
 * look like a valid IMAGE file." on /compress-photos-for-email, which is the
 * page somebody with a phone full of holiday photographs is most likely to
 * arrive at. And the picker's accept attribute is built from this same
 * string, so without the extensions here the file chooser greys the
 * photographs out before anybody can pick one.
 *
 * `image/*` does NOT cover it: it lists no extensions, so namedByAttr has
 * nothing to match, and a browser resolving the pattern still depends on the
 * type the OS failed to supply. */
const HEIC_IN = ",image/heic,image/heif,.heic,.heif"

/* A FOLDER, WHICH IS HOW A PHONE GALLERY HANDS YOU ONE.
 *
 * Select a set in Google Photos and what comes back is a .zip; the same is
 * true of iCloud, of OneDrive and of every desktop's "compress" command. The
 * archive is expanded before it is intake (lib/zipIntake.js) and the pictures
 * re-enter through the one door, so nothing downstream knows about it -- but
 * the PICKER builds its accept attribute from this same string, and without
 * the extension here the file chooser greys the zip out before anybody can
 * choose it. Exactly the half of the HEIC bug that had nothing to do with
 * decoding.
 *
 * Only where the page takes pictures. A document page and a clip page leave
 * it out of their attr and refuse it, which is right: neither has anything to
 * do with a folder of photographs. */
const ZIP_IN = ",application/zip,application/x-zip-compressed,.zip"

/* JPEG XL, WHICH THE CDN READS AND THE OPERATING SYSTEM OFTEN DOES NOT.
 *
 * Measured: a .jxl in either of its two spellings -- the ISO-BMFF container
 * and the bare codestream -- comes back `optimized` from our own CDN for
 * every output format. So the format was always convertible; what refused it
 * was the intake, and for the familiar reason. `image/jxl` is in almost no
 * operating system's extension map yet, so `file.type` is EMPTY and the
 * `includes('image')` test turns away a picture the page can convert -- the
 * HEIC bug exactly, in a newer format. The extension is what namedByAttr()
 * matches on, and it is what stops the file chooser greying the file out. */
const JXL_IN = ",image/jxl,.jxl"

const KIND_RULES = {
    image: {attr: "image/*,application/pdf,.pdf" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: true},
    pdf: {attr: "application/pdf,.pdf", images: false, pdf: true},
    png: {attr: "image/*" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: false},
    webp: {attr: "image/*" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: false},
    gif: {attr: "image/*" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: false},
    jpg: {attr: "image/*" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: false},
    /* Permissive on the container deliberately. An iPhone records HEVC in a
     * .mov and Safari decodes it natively, so refusing on the extension would
     * turn away the commonest video anybody has -- while a desktop browser may
     * well fail on that same file. Which is happening cannot be known from a
     * filename, so the picker offers them and lib/videoFrames.js reports a
     * decode failure in words. */
    /* `produces` is what this page MAKES, and it is here rather than passed
     * into addFiles() because tests/file_intake_test.mjs is right: a switch a
     * caller can set to turn the intake policy off is not worth keeping, and
     * the `derived` flag that used to do it was removed for good reasons.
     *
     * A page that takes a clip has rules.images false, which would refuse the
     * very GIF it exists to produce -- exactly the bug "open on its own" hit
     * on /pdf-to-jpg. Declaring the output alongside the input fixes it
     * without an exemption: an image/gif is admissible HERE because this page
     * makes one, whoever is holding it, and every other image is still
     * refused. */
    video: {attr: "video/*,.mp4,.m4v,.mov,.webm", images: false, pdf: false,
            produces: "image/gif"},
    /* NO `produces` HERE, and the asymmetry with video is the point. A clip
     * page has images:false, so the GIF it makes needs declaring or its own
     * output is refused. An SVG page takes images -- image/svg+xml matches the
     * `includes('image')` test like any other -- so the PNG it makes is
     * already admissible and a second rule would be decoration.
     *
     * The extensions are listed as well as the type because a .svg served off
     * a disk often arrives with an empty type, and the picker's accept
     * attribute is built from this same string. */
    svg: {attr: "image/svg+xml,.svg,.svgz,image/*" + HEIC_IN + ZIP_IN + JXL_IN, images: true, pdf: false},
    /* HEIC is what every iPhone records, and the browser reading it is
     * usually the one that cannot decode it -- Chrome and Firefox both refuse,
     * Safari does not.
     *
     * This entry is no longer the only way in. A .heic is admissible and
     * decoded on EVERY page that takes pictures, because the decoder ships
     * with the application and there is no reason a photograph should be
     * turned away by the page that shrinks photographs for email. What this
     * kind still does is narrow the PICKER to those files and set the page's
     * words; the conversion is the same one, through the same door.
     *
     * The old note here said the file was uploaded and ImageEngine converted
     * it. That was never true in practice -- measured, every directive comes
     * back `origin-object` -- and has not been true in the code since the
     * on-device decoder landed. */
    heic: {attr: "image/heic,image/heif,.heic,.heif,image/*" + ZIP_IN + JXL_IN, images: true, pdf: false},
}

/* A FILE THE PICKER OFFERED, judged by its name.
 *
 * `file.type` comes from the operating system's own extension map and is
 * simply ABSENT often enough to matter -- a .heic off a Windows machine, a
 * .svgz, anything arriving from a zip or a share. The intake test is
 * `type.includes('image')`, so those are refused by a page that explicitly
 * listed their extension in the accept attribute it built from this table.
 *
 * So: if the page's own `attr` names the extension, the file is admissible.
 * Nothing widens for a page whose attr is only a MIME pattern -- `image/*`
 * lists no extensions, so every ordinary page behaves exactly as before. */
export function namedByAttr(file, attr) {
    const name = (file && file.name) || ""
    const dot = name.lastIndexOf(".")
    if (dot < 0) return false
    const ext = name.slice(dot).toLowerCase()
    return String(attr || "").split(",")
        .map((s) => s.trim().toLowerCase())
        .some((s) => s.startsWith(".") && s === ext)
}

export function inputRules() {
    const {kind, max} = taskInput()
    const base = KIND_RULES[kind] || KIND_RULES.image
    // A PDF is a DOCUMENT that becomes many images, so it cannot satisfy a page
    // that takes exactly one IMAGE. /darken-image says {kind: image, max: 1}
    // and the offer of a PDF disappears without anyone naming that page.
    //
    // Only for the generic kind. A page that ASKED for a PDF and takes one of
    // them is /pdf-to-jpg, and an earlier version of this line refused the
    // very file that page exists to accept -- caught by the end-to-end test,
    // which is the reason it was written.
    if (kind === "image" && base.pdf && max === 1) {
        return {attr: "image/*", images: base.images, pdf: false}
    }
    return base
}

/* Whether this page offers the scrim.
 *
 * A page that presets an opacity is a page the control belongs on. Asked by
 * two places now -- the control itself, and the drop zone, which makes the
 * preview bigger when the picture IS the thing being adjusted -- so it lives
 * here rather than being answered twice.
 */
export function taskOffersOverlay() {
    const cfg = (activeTask() || {}).config || {}
    return Object.prototype.hasOwnProperty.call(cfg, "overlayOpacity")
}

/* A task offers the watermark controls by declaring a starting text, even an
 * empty one. Same shape as taskOffersOverlay above: presence in the config is
 * the switch, so a page can ship the controls without pre-filling anybody's
 * words. */
export function taskOffersWatermark() {
    const cfg = (activeTask() || {}).config || {}
    return Object.prototype.hasOwnProperty.call(cfg, "watermarkText")
}

/* And the caption controls, by declaring a starting top caption -- empty being
 * the normal case, since the words are the visitor's. Same shape as the two
 * above: presence in the config is the switch, so the page ships the controls
 * without putting anybody's words in their mouth. */
export function taskOffersMeme() {
    const cfg = (activeTask() || {}).config || {}
    return Object.prototype.hasOwnProperty.call(cfg, "memeTop")
}

export function taskInput() {
    const task = activeTask()
    const raw = (task && task.input) || {}
    const kind = KINDS.includes(raw.kind) ? raw.kind : "image"
    const max = (Number.isInteger(raw.max) && raw.max >= 1) ? raw.max : null
    return {kind, max}
}

/* Which set of words this page should speak in.
 *
 * Not the same thing as `kind`: /png-to-webp and /webp-to-jpg are named for a
 * format but they take images and say "images", so four kinds collapse into
 * two voices plus the singular. One dimension rather than a kind x cardinality
 * matrix, because eight hand-written variants per sentence across ten
 * languages is a translation bill nobody would pay twice.
 *
 * "image" has no copy written for it yet -- no task takes exactly one image
 * until /darken-image does. The lookup falls back to "images" for anything it
 * cannot find, so that page adds strings and changes no code. */
export function inputVoice(cap) {
    const kind = taskInput().kind
    if (kind === "pdf") return "pdf"
    // A page that wants a clip must not tell somebody their clip is not an
    // image. The refusal has to name what the page asked for -- the same
    // reason "pdf" is here.
    if (kind === "video") return "video"
    return cap === 1 ? "image" : "images"
}

/* Whether this page wants a PDF rather than images. Still asked by the parts
 * that are about behaviour rather than wording. */
export function taskAcceptsPdf() {
    return taskInput().kind === "pdf"
}

/* Whether the six sample photographs demonstrate anything.
 *
 * They are JPEGs: exactly the input for "make these smaller", and a dead end
 * dressed as a demo on a page whose subject is converting a PNG or a WebP, or
 * one that wants a PDF. */
export function samplesAreUseful() {
    return taskInput().kind === "image"
}

/* The task's own three steps, or null.
 *
 * The panel used to describe the generic tool on every one of the 110 task
 * pages -- "Upload your images … a batch of 20" on a page about turning one
 * PDF into pages. The registry has had the right three steps per task per
 * language all along; they were only being rendered further down the page.
 *
 * Takes the locale for the same reason taskWords() does, and it is not
 * optional. These strings are baked into the page in the language it was
 * BUILT in, while the interface language is a live preference: a reader on
 * /pdf-to-jpg who switches to Spanish would otherwise get three English steps
 * inside a Spanish interface. A generic sentence in the right language beats a
 * specific one in the wrong one. Same plaster, same real fix -- one URL, one
 * language, everything on it agreeing. */
export function taskSteps(locale) {
    const task = activeTask()
    const steps = task && task.steps
    if (!Array.isArray(steps) || steps.length !== 3) return null
    if (locale && task.lang && locale !== task.lang) return null
    return steps.every((s) => s && s.title && s.body) ? steps : null
}

export function activeTask() {
    if (typeof window === "undefined") return null
    const t = window.OIO_TASK
    return (t && typeof t === "object" && t.slug) ? t : null
}

/* WHICH DRAWER THIS PAGE'S TOOL LIVES IN, or "" on the home page.
 *
 * The drop zone is painted from it: a page about documents is not the same
 * errand as a page about privacy, and the biggest thing on the screen is the
 * one worth saying it with. Generated into the page by tools/make_task_pages.py
 * out of the same list the "All tools" menu groups by, so a tool cannot be
 * filed under one errand in the menu and coloured for another.
 *
 * Narrowed to the keys a stylesheet actually has a rule for, for the reason
 * applyTaskToState() gives below: a page is a static file on a CDN, and a
 * stale or hand-edited one would otherwise put an arbitrary string into an
 * attribute selector. An unknown family is the home page's gradient, which is
 * what every page had until now.
 */
export const TASK_FAMILIES = ["convert", "pdf", "animation", "smaller",
                              "picture", "privacy", "website", "arrange"]

export function taskFamily() {
    const t = activeTask()
    const f = t && typeof t.family === "string" ? t.family : ""
    return TASK_FAMILIES.indexOf(f) >= 0 ? f : ""
}

/* Mutates a freshly built state object in place and returns it.
 *
 * Only the keys named above are honoured, and each is type-checked: the
 * registry is ours, but a page is a static file on a CDN and this is the seam
 * where a stale or hand-edited one would otherwise quietly reconfigure the
 * application into a state the UI cannot show.
 */
export function applyTaskToState(state, buildVariants, catalogOrder) {
    const task = activeTask()
    if (!task || !task.config) return state
    const c = task.config

    for (const key of Object.keys(c)) {
        if (!ALLOWED.includes(key)) continue
        const v = c[key]

        if (key === "formats") {
            if (!Array.isArray(v) || !v.length) continue
            // catalogue order, so the table sequence never depends on how
            // somebody happened to type the list in the registry
            const wanted = new Set(v)
            const formats = catalogOrder.filter((id) => wanted.has(id))
            if (!formats.length) continue
            state.enabledFormats = formats

            /* AND the batch's single format, which is a different field.
             *
             * A batch asks for ONE format -- OptionsPanel shows a select bound
             * to batchFormat, not the checkbox list -- and batchFormat is a
             * plain "jpeg" in state() that nothing derived from the registry.
             * So /jpg-to-webp handed back p0_1280.jpg for two or more images,
             * on a page whose whole subject is WebP, and said "as JPEG" above
             * the table while doing it. Measured; /png-to-webp and
             * /webp-to-png had it too.
             *
             * Taken from the ORDERED list rather than the registry's raw one,
             * so it is the format the app will actually produce first: the
             * catalogue decides sequence, not however the array was typed. */
            state.batchFormat = formats[0]
            // Built at the END, not here: this loop walks the config's keys in
            // whatever order they were written, and how many WIDTHS to make
            // depends on pictureElements -- which for the responsive page is
            // the very next key. Building here asked the question before the
            // answer existed.
            continue
        }

        if (key === "preset" || key === "overlayColor" || key === "watermarkColor"
                || key === "watermarkText" || key === "watermarkPosition"
                || key === "watermarkFont" || key === "memeFont"
                || MEME_TEXT_KEYS.includes(key) || MEME_COLOR_KEYS.includes(key)) {
            if (v === null || typeof v === "string") state[key] = v
            continue
        }
        if (key === "watermarkSize" || key === "watermarkOpacity"
                || MEME_SIZE_KEYS.includes(key)) {
            // Range is enforced again in usableWatermark() and usableMeme();
            // this only keeps the store's shape honest.
            if (Number.isFinite(v) && v >= 0 && v <= 1) state[key] = v
            continue
        }
        if (key === "overlayOpacity") {
            // Range is enforced again in usableOverlay(); this only keeps the
            // store's shape honest.
            if (Number.isFinite(v) && v >= 0 && v <= 1) state.overlayOpacity = v
            continue
        }
        if (key === "cmpr") {
            if (v === null || Number.isFinite(v)) state.cmpr = v
            continue
        }
        if (key === "pct") {
            if (Number.isFinite(v)) state.pct = v
            continue
        }
        if (typeof v === "boolean") {
            state[key] = v
            continue
        }

        /* EVERYTHING ELSE, MATCHED AGAINST THE TYPE THE STORE ALREADY HOLDS.
         *
         * There was no branch here at all, and the hole was live: /image-to-ascii
         * has shipped `{"asciiCols": 100, "asciiRamp": "classic"}` since the page
         * was written, and applyTaskToState() dropped BOTH -- a number and a
         * string, neither named in the branches above, falling through a
         * boolean test to nothing. Measured by driving the real function with
         * `{asciiCols: 250, asciiRamp: "blocks", asciiColours: true}`: the
         * boolean landed and the other two came back 100 and "classic".
         *
         * It was invisible because those two values ARE the store's defaults,
         * so the page has always looked right. The first task to preset a
         * number that differs would have been silently ignored, which is the
         * same class of fault as the hand-typed CATALOG in task_config_test.mjs
         * that compared jpeg against jpeg and passed while the page was
         * misconfigured.
         *
         * MATCHED AGAINST state[key] RATHER THAN LISTED BY NAME, deliberately.
         * A list of key names is a second copy of the store's shape and it is
         * what drifts -- the argument currentSettings() makes for coercing at
         * one boundary rather than in set(). Asking whether the store already
         * holds a string or a number needs no maintenance and refuses a config
         * whose type disagrees, which is the right failure: a page is a static
         * file on a CDN and a stale one must not be able to put an arbitrary
         * shape into the store.
         */
        if (typeof v === "string" && typeof state[key] === "string") {
            state[key] = v
            continue
        }
        if (Number.isFinite(v) && typeof state[key] === "number") {
            state[key] = v
        }
    }

    /* Now that every key has been applied.
     *
     * /responsive-image-generator asks for three formats AND the picture
     * element, which means three formats at two widths: six files and the
     * markup, which is what the page promises in so many words. It was
     * delivering three -- the variant list was built while handling "formats",
     * before "pictureElements" had been read, and with no width argument, so it
     * took the single-width default.
     *
     * The caller decides what the widths are, because the store's own
     * sizesWanted getter already decides it for every other rebuild and two
     * places deciding is how they come to disagree.
     */
    if (Array.isArray(state.enabledFormats) && state.enabledFormats.length) {
        state.imageVersions = buildVariants(state.enabledFormats, !!state.pictureElements)
    }
    return state
}
